import { useState, useEffect } from "react";
import { CreditCard, AlertTriangle, Check, Clock, X, Plus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { EditPaymentMethodModal } from "./EditPaymentMethodModal";
import { invokeTestCharge } from "@/lib/fanbasis";

interface ClientPaymentMethod {
  id: string;
  fanbasis_payment_method_id: string;
  last4: string;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_primary: boolean | null;
  status: string | null;
}

interface ClientWithPayment {
  id: string;
  name: string;
  email: string;
  fanbasis_customer_id: string | null;
  paymentMethods: ClientPaymentMethod[];
}

export function PaymentMethodsTab({ dataVersion = 0 }: { dataVersion?: number }) {
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clientsWithPayment, setClientsWithPayment] = useState<ClientWithPayment[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    fetchClientsWithPayments();
  }, [dataVersion]);

  const fetchClientsWithPayments = async () => {
    setLoading(true);
    try {
      // Fetch clients with their payment methods
      const { data: clients, error: clientsError } = await supabase
        .from("clients")
        .select("id, company_name, email, fanbasis_customer_id")
        .order("company_name");

      if (clientsError) throw clientsError;

      const { data: paymentMethods, error: pmError } = await supabase
        .from("payment_methods")
        .select("*");

      if (pmError) throw pmError;

      // Group payment methods by client
      const clientsMap = new Map<string, ClientWithPayment>();
      
      clients?.forEach(client => {
        const clientPayments = paymentMethods?.filter(pm => pm.client_id === client.id) || [];
        if (clientPayments.length > 0) {
          clientsMap.set(client.id, {
            id: client.id,
            name: client.company_name,
            email: client.email,
            fanbasis_customer_id: client.fanbasis_customer_id ?? null,
            paymentMethods: clientPayments.map(pm => ({
              id: pm.id,
              fanbasis_payment_method_id: pm.fanbasis_payment_method_id,
              last4: pm.last4,
              brand: pm.brand,
              exp_month: pm.exp_month,
              exp_year: pm.exp_year,
              is_primary: pm.is_primary,
              status: pm.status
            }))
          });
        }
      });

      setClientsWithPayment(Array.from(clientsMap.values()));
    } catch (error) {
      console.error("Error fetching payment data:", error);
      toast.error("Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  };

  const activeCards = clientsWithPayment.filter(c => 
    c.paymentMethods.some(pm => pm.status === 'active')
  );
  
  const expiringSoon = clientsWithPayment.filter(c => 
    c.paymentMethods.some(pm => {
      if (!pm.exp_month || !pm.exp_year) return false;
      const expDate = new Date(pm.exp_year, pm.exp_month - 1);
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
      return expDate <= threeMonthsFromNow && pm.status === 'active';
    })
  );
  
  const expired = clientsWithPayment.filter(c => 
    c.paymentMethods.some(pm => pm.status === 'expired')
  );
  
  const failed = clientsWithPayment.filter(c => 
    c.paymentMethods.some(pm => pm.status === 'failed')
  );

  const getCardIcon = () => {
    return <CreditCard className="w-5 h-5" />;
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'active':
        return <BadgeVenture variant="success"><Check className="w-3 h-3 mr-1" />Active</BadgeVenture>;
      case 'expired':
        return <BadgeVenture variant="error"><Clock className="w-3 h-3 mr-1" />Expired</BadgeVenture>;
      case 'failed':
        return <BadgeVenture variant="error"><X className="w-3 h-3 mr-1" />Failed</BadgeVenture>;
      default:
        return <BadgeVenture variant="default">Unknown</BadgeVenture>;
    }
  };

  const handleUpdate = (clientId: string, clientName: string) => {
    setSelectedClient({ id: clientId, name: clientName });
    setEditModalOpen(true);
  };

  const handleAddBackupCard = async (clientId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('fanbasis-connect', {
        body: { clientId, action: 'add_payment_method' },
      });
      if (error) throw error;
      if (data?.checkoutUrl) {
        window.open(data.checkoutUrl, '_blank');
        toast.success('Fanbasis checkout opened in new tab');
      } else {
        toast.info('Fanbasis add-card flow coming soon');
      }
    } catch (err) {
      console.error('Add backup card error:', err);
      toast.error('Failed to open card checkout');
    }
  };

  const handleReplaceCard = async (clientId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('fanbasis-connect', {
        body: { clientId, action: 'replace_payment_method' },
      });
      if (error) throw error;
      if (data?.checkoutUrl) {
        window.open(data.checkoutUrl, '_blank');
        toast.success('Fanbasis card replacement opened in new tab');
      } else {
        toast.info('Replacement flow not yet available from Fanbasis');
      }
    } catch (err) {
      console.error('Replace card error:', err);
      toast.error('Failed to open card replacement');
    }
  };

  const handleChargeNow = async (
    client: ClientWithPayment,
    pm: { fanbasis_payment_method_id: string },
  ) => {
    if (!client.fanbasis_customer_id) return; // defense-in-depth; button is disabled
    const result = await invokeTestCharge({
      client: {
        id: client.id,
        company_name: client.name,
        fanbasis_customer_id: client.fanbasis_customer_id,
      },
      paymentMethod: pm,
      amountCents: 100,
    });
    if (result.ok) {
      toast.success(`Charge succeeded for ${client.name}`);
    } else {
      toast.error(`Charge failed for ${client.name}: ${result.error}`);
    }
  };

  const handleModalSuccess = () => {
    fetchClientsWithPayments();
  };

  return (
    <TooltipProvider>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Payment Methods</h2>
          <p className="text-sm text-muted-foreground">Manage payment methods for all clients</p>
        </div>
        <Button variant="outline" onClick={() => setShowBulkActions(!showBulkActions)}>
          Bulk Actions
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/20">
              <Check className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activeCards.length}</p>
              <p className="text-sm text-muted-foreground">Active Cards</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-500/20">
              <Clock className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{expiringSoon.length}</p>
              <p className="text-sm text-muted-foreground">Expiring Soon</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/20">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{expired.length}</p>
              <p className="text-sm text-muted-foreground">Expired</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/20">
              <X className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{failed.length}</p>
              <p className="text-sm text-muted-foreground">Failed Charges</p>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Payment Methods Table */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>All Payment Methods</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading payment methods...</p>
            </div>
          ) : clientsWithPayment.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No payment methods on file</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Payment Methods</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Expiry</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clientsWithPayment.map((client) => {
                    const primaryMethod = client.paymentMethods.find(pm => pm.is_primary) || client.paymentMethods[0];
                    const methodCount = client.paymentMethods.length;
                    const activePrimary = client.paymentMethods.find(
                      (pm) => pm.is_primary && pm.status === 'active',
                    );
                    const chargeable = !!activePrimary && !!client.fanbasis_customer_id;

                    return (
                      <tr key={client.id} className="border-b border-border hover:bg-surface transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{client.name}</p>
                            <p className="text-sm text-muted-foreground">{client.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {getCardIcon()}
                            <span>
                              {primaryMethod?.brand || "Card"} •••• {primaryMethod?.last4}
                              {methodCount > 1 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (+{methodCount - 1} more)
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span>
                            {primaryMethod?.exp_month}/{primaryMethod?.exp_year?.toString().slice(-2)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(primaryMethod?.status)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdate(client.id, client.name)}
                            >
                              Manage
                            </Button>
                            {(primaryMethod?.status === 'expired' || primaryMethod?.status === 'failed') && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleReplaceCard(client.id)}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Replace Card
                              </Button>
                            )}
                            <Button
                              variant="gradient-outline"
                              size="sm"
                              onClick={() => handleAddBackupCard(client.id)}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Card
                            </Button>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={!chargeable}
                                    onClick={() =>
                                      chargeable &&
                                      activePrimary &&
                                      handleChargeNow(client, {
                                        fanbasis_payment_method_id:
                                          activePrimary.fanbasis_payment_method_id,
                                      })
                                    }
                                  >
                                    Charge Now
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {!chargeable && (
                                <TooltipContent>No payment method saved</TooltipContent>
                              )}
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Edit Payment Method Modal */}
      {selectedClient && (
        <EditPaymentMethodModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
    </TooltipProvider>
  );
}
